<template>
  <div class="modal-container">
    <div class="modal-content" v-if="!loading">
      <div class="mb-3">
        <div class="has-text-weight-semibold is-size-6 mb-5">
          Add Geofence
        </div>
        <div class="has-text-danger has-text-weight-semibold is-size-7 mb-2">
          {{ msg }}
        </div>
        <div class="field mb-2">
          <label class="label is-size-7 mb-1">Information</label>
          <div class="content-box p-2">
            <div class="is-size-7 has-text-weight-semibold mb-2">
              Zone: {{ shape?.zone || "" }}
            </div>
            <div class="is-size-7 has-text-weight-semibold mb-2">
              Distribution Zone: {{ shape?.hub?.DistributionZoneName || "" }}
            </div>
          </div>
        </div>
      </div>

      <!-- Buttons container -->
      <div class="buttons-container">
        <div class="buttons is-centered">
          <button class="button is-small" @click="cancelEdit()">Cancel</button>
          <button v-if="editable" class="button is-small is-success" @click="editFirestore">Save</button>
        </div>
      </div>
    </div>
    <div class="loader-container" v-else>
      <PulseLoader />
    </div>
  </div>
</template>
<script>
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import EventHandler from "../EventHandler";

const SINGLE_SHAPE = "SINGLE_SHAPE";
const SINGLE_SHAPE_WITH_HOLES = "SINGLE_SHAPE_WITH_HOLES";
const MULTI_SINGLE_SHAPE = "MULTI_SINGLE_SHAPE";
const MULTI_SINGLE_WITH_HOLES = "MULTI_SINGLE_WITH_HOLES";

/**
* EditFSAZoneModal Component
*
* Provides interface for editing existing zone shapes and saving changes
*/
export default {
  components: {
    PulseLoader,
  },
  data() {
    return {
      msg: "",
      loading: false,
    };
  },
  props: {
    shapesToAdd: {
      type: [Array, Object],
    },
    shape: {
      type: Object,
    },
    shapeType: {
      type: String,
    },
    editable: {
      type: Boolean,
    },
  },
  methods: {
    /**
     * Closes the modal and resets the state
     */
    closeModal() {
      EventHandler.$emit("closeModal");
      this.close();
      this.editNameMode = false;
    },
    /**
     * Handles cancel edit action
     */
    cancelEdit() {
      this.close();
    },
    /**
     * Saves the coordinates for different shape types
     * @param {Object|Array} shapesToAdd - The shapes to be added
     * @param {String} shapeId - The ID of the shape
     * @param {String} shapeType - The type of shape
     */
    async saveCoordinates(shapesToAdd, shapeId, shapeType) {
      if (shapeType === SINGLE_SHAPE) {
        const coordinates = shapesToAdd?.coordinates?.[0];
        const fsa = shapesToAdd?.fsa;
        try {
          await this.updateZone({
            coordinates,
            fsa,
            shapeId,
            multi: false,
          });
          await this.removeShape();
          this.loading = false;
        } catch (err) {
          this.loading = false;
          this.msg = "Error in editing zone";
        }
      } else if (shapeType === SINGLE_SHAPE_WITH_HOLES) {
        const coordinates = shapesToAdd?.coordinates;
        const fsa = shapesToAdd?.fsa;
        try {
          await this.updateZone({
            coordinates,
            fsa,
            shapeId,
            multi: true,
          });
          await this.removeShape();
          this.loading = false;
        } catch (err) {
          this.loading = false;
          this.msg = "Error in editing zone";
        }
      } else if (shapeType === MULTI_SINGLE_SHAPE) {
        const selectedShape = this.shape;
        this.updateMultipleZone({
          newShapes: shapesToAdd,
          currentShape: selectedShape,
          shapeType: MULTI_SINGLE_SHAPE,
        });
        await this.removeShape();
      } else {
        const selectedShape = this.shape;
        this.updateMultipleZone({
          newShapes: shapesToAdd,
          currentShape: selectedShape,
          shapeType: MULTI_SINGLE_WITH_HOLES,
        });
        await this.removeShape();
      }
    },
    /**
     * Handles saving edited shape to Firestore
     */
    async editFirestore() {
      this.activateEditOnProcess();
      this.loading = true;
      const shapeType = this.shapeType?.trim();
      const shapeId = this.shape?.id?.trim();
      const shapesToAdd = this.shapesToAdd;
      this.saveCoordinates(shapesToAdd, shapeId, shapeType);
    },
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
/* Target only our specific EditFSAZoneModal popup with the class we'll add */
.edit-fsa-zone-modal-popup .leaflet-popup-content-wrapper {
  padding: 20px !important;
  border-radius: 4px !important;
  width: 450px !important;
}

.edit-fsa-zone-modal-popup .leaflet-popup-content {
  width: auto !important;
  max-width: 450px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.edit-fsa-zone-modal-popup .leaflet-popup-tip {
  width: 10px !important;
  height: 10px !important;
  margin: -5px auto 0 !important;
}

.edit-fsa-zone-modal-popup .leaflet-popup-content>* {
  margin: 0 !important;
  padding: 0 !important;
}

.edit-fsa-zone-modal-popup .leaflet-popup-close-button {
  display: none !important;
}
</style>

<style lang="scss" scoped>
@import "~leaflet/dist/leaflet.css";

/* Modal container for reduced width */
.modal-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

.modal-content {
  width: 100%;
  max-width: 450px;
  padding: 8px !important;
  display: flex;
  flex-direction: column;
  min-height: 300px;

  >div:first-child {
    flex-grow: 1;
  }
}

/* Button container to push buttons to bottom */
.buttons-container {
  margin-top: auto;
  padding-top: 1rem;
}

.loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0;
  margin: 0;
}

.content-box {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background-color: #f8f8f8;
}

.custom .leaflet-popup-tip,
.custom .leaflet-popup-content-wrapper {
  background: #e93434;
  color: #ffffff;
}

/* Button styling */
.buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: center;

  .button {
    margin: 0 !important;
    flex: 0 0 auto;
    min-width: 80px;
    height: 28px !important;
    font-size: 0.75rem !important;
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }
}

.label.is-size-7 {
  color: #363636;
  font-weight: 600;
}

.field {
  margin-bottom: 0.75rem;
}
</style>
